
import { navigateTo, initNavigation, setCurrentUser, getCurrentUser } from './ui/router.js';
import { initTheme } from './ui/theme.js';
import { initHardwareScanner } from './ui/hardware.js';
import { toggleFlashlight, processScanResult, initCameraUIHandlers } from './ui/scanner.js';
import { getConfig, saveConfigRemote, syncConfigFromServer, getCapacity, setCapacity } from './services/config.js';
import { getBlacklist, addToBlacklist, removeFromBlacklist, generateMockScans, clearAllScans, getAllScans, verifyUserCredentials } from './infra/db.js';
import { hashDNI } from './infra/crypto.js';
import { parsePDF417, parseMRZ } from './services/parser.js';
import { initReportsModule } from './ui/reports.js';
import { initAssistantsModule } from './ui/assistants.js';
import { initUsersModule } from './ui/users.js';
import { initCompaniesModule } from './ui/companies.js';
import { initAlertsAndErrors } from './ui/errors.js';

import loginHtml from './templates/login.html?raw';
import scanHtml from './templates/scan.html?raw';
import reportsHtml from './templates/reports.html?raw';
import assistantsHtml from './templates/assistants.html?raw';
import usersHtml from './templates/users.html?raw';
import configHtml from './templates/config.html?raw';

document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('main-view-container');
  if (container) {
    container.innerHTML = loginHtml + scanHtml + reportsHtml + assistantsHtml + usersHtml + configHtml;
  }
  initReportsModule();
  initAssistantsModule();
  initUsersModule();
  initCompaniesModule();

  initAlertsAndErrors();
  initNavigation();
  initTheme();
  initLoginHandler();
  initHardwareScanner(async (rawData) => {
    const parsed = parsePDF417(rawData) || parseMRZ(rawData) || { dni: rawData.substring(0, 8).trim(), fullName: 'Desconocido', gender: 'U', birthDate: null };
    await processScanResult(parsed, 'HARDWARE');
  });
  initUIHandlers();
  registerServiceWorker();
  navigateTo('login');
});

// Controla el formulario de inicio de sesión
function initLoginHandler() {
  const form = document.getElementById('login-form');
  if (!form) return;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const user = document.getElementById('login-username').value.trim();
    const pass = document.getElementById('login-password').value.trim();
    try {
      const dbUser = await verifyUserCredentials(user, pass);
      if (dbUser && dbUser.role) {
        loginSuccess(dbUser);
      } else {
        alert('Credenciales incorrectas o cuenta inactiva.');
      }
    } catch (err) {
      console.warn('Error al validar en BD (usando credenciales de respaldo):', err);
      if (user === 'superadmin' && pass === 'superadmin') {
        loginSuccess({ username: user, fullName: 'Super Administrador Global', role: 'superadmin', company_id: null });
      } else if (user === 'superadmin1' && pass === 'superadmin') {
        loginSuccess({ username: user, fullName: 'SuperAdmin Empresa A', role: 'superadmin', company_id: 1 });
      } else if (user === 'superadmin2' && pass === 'superadmin') {
        loginSuccess({ username: user, fullName: 'SuperAdmin Empresa B', role: 'superadmin', company_id: 2 });
      } else if (user === 'admin' && pass === 'admin') {
        loginSuccess({ username: user, fullName: 'Administrador Puerta A', role: 'admin', company_id: 1 });
      } else if (user === 'portero' && pass === 'portero') {
        loginSuccess({ username: user, fullName: 'Portero Acceso A', role: 'portero', company_id: 1 });
      } else {
        alert(`Error de conexión a la BD: ${err.message || err}`);
      }
    }
  });
}

function loginSuccess(userObj) {
  setCurrentUser(userObj);
  // Sincronizar configuración desde el servidor si está en modo remoto
  syncConfigFromServer(userObj.company_id).catch(() => {});
  navigateTo('scan');
}

// Registra listeners de botones de control y simulación
function initUIHandlers() {
  document.getElementById('btn-toggle-flashlight')?.addEventListener('click', () => toggleFlashlight());
  initCameraUIHandlers();
  window.addEventListener('capacity_changed', refreshCapacityUI);
  refreshCapacityUI();
  window.addEventListener('load_config_settings', loadConfigValues);
  initSettingsForm();
  initBlacklistManager();
  initSimulator();
  initMockDataHandlers();
}

// Refresca la barra de asistentes en la cabecera y el escáner con la BD
async function refreshCapacityUI() {
  let count = 0;
  try {
    const user = getCurrentUser();
    const companyId = user ? user.company_id : null;
    if (user) {
      await syncConfigFromServer(companyId).catch(() => {});
    }
    const scans = await getAllScans(companyId);
    count = scans.filter(s => s.status === 'ALLOWED').length;
    setCapacity(count);
  } catch (err) {
    console.error('Error al sincronizar asistentes:', err);
    count = getCapacity();
  }
  const config = getConfig();
  document.querySelectorAll('.live-capacity').forEach(el => el.innerText = count.toString());
  document.querySelectorAll('.max-capacity').forEach(el => el.innerText = config.maxCapacity.toString());

  const progress = document.getElementById('capacity-progress-bar');
  if (progress) {
    const percent = Math.min(100, (count / config.maxCapacity) * 100);
    progress.style.width = `${percent}%`;
    progress.className = percent >= 90 ? 'danger' : percent >= 75 ? 'warning' : 'success';
  }

  const badgeText = document.getElementById('header-capacity-badge-text');
  if (badgeText) {
    badgeText.innerHTML = config.showLimit !== false
      ? `Asistentes: <span class="live-capacity">${count}</span> / <span class="max-capacity">${config.maxCapacity}</span>`
      : `Asistentes: <span class="live-capacity">${count}</span>`;
  }
}

// Carga valores guardados en los inputs de configuración
function loadConfigValues() {
  const config = getConfig();
  document.getElementById('cfg-secret-word').value = config.secretWord;
  document.getElementById('cfg-max-capacity').value = config.maxCapacity;
  document.getElementById('cfg-frequent-threshold').value = config.frequentThreshold;
  document.getElementById('cfg-current-capacity').value = getCapacity();
  document.getElementById('cfg-db-mode').value = config.dbMode || 'local';
  document.getElementById('cfg-api-url').value = config.apiUrl || '';
  document.getElementById('cfg-show-limit').checked = config.showLimit !== false;

  // Mostrar/ocultar campos avanzados y simulador según el rol
  const user = getCurrentUser();
  const isSuper = user && user.role === 'superadmin';
  const toggleDisplay = (id, show) => {
    const el = document.getElementById(id);
    if (el) el.style.display = show ? 'block' : 'none';
  };
  toggleDisplay('group-secret-word', isSuper);
  toggleDisplay('group-db-mode', isSuper);
  toggleDisplay('group-api-url', isSuper);

  const simulatorPanel = document.querySelector('.simulator-panel');
  if (simulatorPanel) {
    simulatorPanel.style.display = isSuper ? 'block' : 'none';
  }

  renderBlacklistUI();
}

function initSettingsForm() {
  const form = document.getElementById('config-form');
  if (!form) return;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const secret = document.getElementById('cfg-secret-word').value.trim();
    const maxCap = parseInt(document.getElementById('cfg-max-capacity').value, 10);
    const freq = parseInt(document.getElementById('cfg-frequent-threshold').value, 10);
    const curCap = parseInt(document.getElementById('cfg-current-capacity').value, 10);
    const dbMode = document.getElementById('cfg-db-mode').value;
    const apiUrl = document.getElementById('cfg-api-url').value.trim();
    const showLimit = document.getElementById('cfg-show-limit').checked;
    const newCfg = { secretWord: secret, maxCapacity: maxCap, frequentThreshold: freq, dbMode, apiUrl, showLimit };

    const user = getCurrentUser();
    await saveConfigRemote(newCfg, user ? user.company_id : null);
    setCapacity(curCap);
    alert('✅ Configuración guardada y sincronizada correctamente.');
    refreshCapacityUI();
  });
}

function initMockDataHandlers() {
  document.getElementById('btn-generate-mock-data')?.addEventListener('click', async () => {
    try {
      const user = getCurrentUser();
      await generateMockScans(user ? user.company_id : null);
      alert('⚡ 50 registros de prueba generados exitosamente.');
      await refreshCapacityUI();
    } catch (err) {
      alert(`Error al generar datos: ${err.message}`);
    }
  });

  document.getElementById('btn-clear-mock-data')?.addEventListener('click', () => {
    const isLight = document.documentElement.classList.contains('light-mode');
    Swal.fire({
      title: '¿Confirmar eliminación?',
      text: '¿Está seguro de eliminar TODOS los registros de escaneo? Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      background: isLight ? '#ffffff' : '#171f33',
      color: isLight ? '#0f172a' : '#dae2fd'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const user = getCurrentUser();
          await clearAllScans(user ? user.company_id : null);
          alert('🗑️ Registros de escaneo eliminados.');
          await refreshCapacityUI();
        } catch (err) {
          alert(`Error al limpiar base de datos: ${err.message}`);
        }
      }
    });
  });
}

async function initBlacklistManager() {
  const addBtn = document.getElementById('btn-blacklist-add');
  if (!addBtn) return;
  addBtn.addEventListener('click', async () => {
    const dni = document.getElementById('blacklist-dni').value.trim();
    const name = document.getElementById('blacklist-name').value.trim();
    const reason = document.getElementById('blacklist-reason').value.trim();
    if (!dni || !name) return alert('DNI y Nombre son obligatorios.');

    const config = getConfig();
    const hash = await hashDNI(dni, config.secretWord);
    const user = getCurrentUser();
    const companyId = user ? user.company_id : null;
    await addToBlacklist(hash, name, reason, companyId);
    document.getElementById('blacklist-dni').value = '';
    document.getElementById('blacklist-name').value = '';
    document.getElementById('blacklist-reason').value = '';
    renderBlacklistUI();
  });
}

async function renderBlacklistUI() {
  const user = getCurrentUser();
  const companyId = user ? user.company_id : null;
  const list = await getBlacklist(companyId);
  const tbody = document.getElementById('table-blacklist-body');
  if (!tbody) return;
  tbody.innerHTML = list.length ? list.map(item => `
    <tr>
      <td data-label="Hash" class="font-mono text-xs">${item.hashedDni.substring(0, 12)}...</td>
      <td data-label="Nombre">${item.fullName}</td>
      <td data-label="Motivo">${item.reason || 'Sin motivo'}</td>
      <td data-label="Acción">
        <button class="btn btn-sm btn-danger btn-remove-blacklist" data-hash="${item.hashedDni}">Quitar</button>
      </td>
    </tr>
  `).join('') : '<tr><td colspan="4" class="text-center text-muted">Lista negra vacía.</td></tr>';

  tbody.querySelectorAll('.btn-remove-blacklist').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      await removeFromBlacklist(e.currentTarget.getAttribute('data-hash'), companyId);
      renderBlacklistUI();
    });
  });
}

// Inicializa el Simulador de escaneos para desarrollo
function initSimulator() {
  document.getElementById('btn-simulate-scan')?.addEventListener('click', async () => {
    try {
      const type = document.getElementById('sim-type').value;
      const input = document.getElementById('sim-input').value;
      if (!input.trim()) return alert('Ingrese una cadena de datos para simular.');
      const parsed = type === 'PDF417' ? parsePDF417(input) : parseMRZ(input);
      if (parsed) {
        await processScanResult(parsed, 'SIM_' + type);
      } else {
        alert(`Formato de ${type} simulado inválido. Revise la estructura.`);
      }
    } catch (err) {
      alert(`Error en simulación: ${err.message}`);
    }
  });

  document.querySelectorAll('.simulator-panel pre').forEach(pre => {
    pre.style.cursor = 'pointer';
    pre.title = 'Haga clic para cargar en el simulador';
    pre.addEventListener('click', (e) => {
      const txt = e.currentTarget.innerText;
      document.getElementById('sim-input').value = txt;
      document.getElementById('sim-type').value = txt.includes('\n') ? 'MRZ' : 'PDF417';
    });
  });
}

function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('./sw.js')
      .then(reg => console.log('Service Worker registrado con scope:', reg.scope))
      .catch(err => console.warn('Fallo en registro de SW:', err));
  }
}
